/**
 * Geolocation types and API client.
 *
 * Type definitions and API client for geolocation functionality.
 */

import { mixpanel } from "common/analytics";

export type Postcode = {
  code: string;
  formattedCode: string;
  countryCode: string;
  latitude: number;
  longitude: number;
};

export function instanceOfPostcode(object: unknown): object is Postcode {
  return typeof object === "object" && object !== null && "code" in object;
}

export type Location = { latitude: number; longitude: number };

export type IPLookup = {
  latitude: number;
  longitude: number;
  country: string;
};

export class GeolocationAPI {
  baseUrl: string;
  constructor() {
    this.baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v1/`;
  }

  async getPostcodes(params?: URLSearchParams): Promise<Postcode[]> {
    mixpanel.track("Get postcode attempted");
    const res = await fetch(`${this.baseUrl}postcodes?${params}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });

    if (res.status !== 200) {
      mixpanel.track("Get postcode failed");
      throw new Error("Unable to load postcode");
    }
    mixpanel.track("Get postcode successful");
    return (await res.json()) as Postcode[];
  }

  // Note: makes a single geolocation api request,
  // we assume the user doesn't move while using
  // the app here; if this assumption changes we
  // need to use geolocation.watchPosition
  async getNavigatorGeolocation(): Promise<Location | undefined> {
    mixpanel.track("Geolocation attempted");
    if (navigator.geolocation) {
      return new Promise<Location | undefined>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            mixpanel.track("Geolocation successful");
            resolve({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            });
          },
          (err: GeolocationPositionError) => {
            mixpanel.track("Geolocation failed");
            reject(new Error(err.code + ": " + err.message));
          }
        )
      );
    } else {
      mixpanel.track("Geolocation disabled");
      return Promise.reject(new Error("Geolocation disabled"));
    }
  }

  async getIPLocation(): Promise<IPLookup | undefined> {
    mixpanel.track("Get ip location attempted");
    const res = await fetch(`${this.baseUrl}geoip`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });

    if (res.status !== 200) {
      mixpanel.track("Get ip location failed");
      throw new Error(`Unable to load geo ip location`);
    }

    mixpanel.track("Get ip location successful");
    return (await res.json()) as IPLookup;
  }
}

export const geolocationAPI = new GeolocationAPI();

/**
 * Fetch postcodes by code.
 */
export async function fetchPostcodes(code: string): Promise<Postcode[]> {
  return geolocationAPI.getPostcodes(new URLSearchParams({ code }));
}

/**
 * Get navigator geolocation.
 */
export async function getNavigatorGeolocation(): Promise<Location | undefined> {
  return geolocationAPI.getNavigatorGeolocation();
}

/**
 * Fetch IP location.
 */
export async function fetchIPLocation(): Promise<IPLookup | undefined> {
  return geolocationAPI.getIPLocation();
}
